import React, { useState, useCallback, useEffect, useRef } from 'react';
import { useDropzone } from 'react-dropzone';
import { useNavigate } from 'react-router-dom';
import api from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import { useSnackbar } from 'notistack';
import { theme } from '../../styles/theme';
import CustomButton from '../../components/ui/CustomButton';
import DropZone from '../../components/ui/DropZone';
import FileList, { FileItem } from '../../components/ui/FileList';
import ProgressBar from '../../components/ui/ProgressBar';
import Modal from '../../components/ui/Modal';
import AlertBox from '../../components/ui/AlertBox';
import Badge from '../../components/ui/Badge';
import {
  acknowledgeStoredFile,
  BatchRow,
  beginBatchUpload,
  BULK_UPLOAD_CONCURRENCY,
  canClearStoredBatch,
  clearStoredBatch,
  combineSelectionWarnings,
  countNotUploaded,
  describeUploadError,
  failStoredFile,
  forgetAnsweredStarts,
  formatFileSize,
  formatResumeTime,
  generateUploadBatchId,
  holdRowsBeingStarted,
  initialRowFromUpload,
  isBatchUploading,
  isPollingDone,
  isStillSending,
  isResumableLatestBatch,
  loadStoredBatch,
  MAX_ACCEPTED_FILE_BYTES,
  mergeBatchRows,
  nextPollDelay,
  NOT_PDF_MESSAGE,
  notUploadedMessage,
  parseUploadConfig,
  parseUploadStatus,
  RejectedFile,
  rowsFromStoredBatch,
  runWithConcurrency,
  settlePendingRows,
  DEFAULT_UPLOAD_MODE,
  sortOutSelection,
  startStoredBatch,
  storedBatchToReopen,
  summariseRows,
  tooLargeMessage,
  uploadFailureRow,
  UploadModeConfig,
} from '../../utils/bulkUpload';

interface UploadedReport {
  reportId: string;
  fileName: string;
  fileSize: number;
  status: 'uploaded' | 'processing' | 'completed' | 'error';
  error?: string;
  processingTime?: number;
}

// Bulk mode: one upload request per file
interface UploadItem {
  state: 'pending' | 'uploading' | 'uploaded' | 'failed';
  progress?: number;
  error?: string;
}

type BulkUploadOutcome =
  | { ok: true; reportId: string; fileName: string; fileSize: number; queued: boolean }
  | { ok: false; error: string };

const toUploadFileStatus = (item: UploadItem): Partial<FileItem> => {
  switch (item.state) {
    case 'pending':
      return { status: 'upload_pending' };
    case 'uploading':
      return { status: 'uploading', uploadProgress: item.progress };
    case 'uploaded':
      return { status: 'queued' };
    default:
      return { status: 'failed', error: item.error };
  }
};

const toBatchFileItem = (row: BatchRow): FileItem => ({
  name: row.fileName,
  size: row.fileSize,
  status: row.state,
  error: row.error,
  failedPages: row.failedPageCount,
  resumeTime: formatResumeTime(row.resumesAt) ?? undefined,
});

const UploadReport: React.FC = () => {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const { user } = useAuth();
  const userId = user?.id;

  const [activeStep, setActiveStep] = useState(0);
  const [files, setFiles] = useState<File[]>([]);
  const [uploading, setUploading] = useState(false);
  const [uploadedReports, setUploadedReports] = useState<UploadedReport[]>([]);
  const [processing, setProcessing] = useState(false);
  const [currentProcessingIndex, setCurrentProcessingIndex] = useState<number>(-1);
  const [showSuccessDialog, setShowSuccessDialog] = useState(false);
  const [batchStartTime, setBatchStartTime] = useState<number | null>(null);
  const [currentElapsedTime, setCurrentElapsedTime] = useState<number>(0);
  const [uploadConfig, setUploadConfig] = useState<UploadModeConfig>(DEFAULT_UPLOAD_MODE);
  const bulkUploadDisabled = !uploadConfig.bulkMode;
  const maxBulkFiles = uploadConfig.maxBulkFiles;

  // Bulk mode: per-file upload progress, aligned with `files`
  const [uploadItems, setUploadItems] = useState<UploadItem[]>([]);

  // Step 3 when the server queues processing: statuses are read back from the server
  const [trackedBatchId, setTrackedBatchId] = useState<string | null>(null);
  const [batchRows, setBatchRows] = useState<BatchRow[]>([]);
  const batchRowsRef = useRef<BatchRow[]>([]);

  // Set once the nurse picks files, so a late return-visit check never moves her off her work
  const interactedRef = useRef(false);

  // /process calls this page started itself (server queue off): report id -> when the call answered (null: not yet)
  const processStartsRef = useRef<Map<string, number | null>>(new Map());
  // Asks the step-3 poll to run now (set while step 3 is polling)
  const pollNowRef = useRef<(() => void) | null>(null);

  const startTracking = useCallback((batchId: string, rows: BatchRow[]) => {
    batchRowsRef.current = rows;
    setBatchRows(rows);
    setTrackedBatchId(batchId);
    setActiveStep(2);
  }, []);

  const stopTracking = () => {
    batchRowsRef.current = [];
    setBatchRows([]);
    setTrackedBatchId(null);
  };

  // Fetch config to check if bulk upload is enabled; when the server queues processing,
  // reopen the latest unfinished upload
  useEffect(() => {
    let cancelled = false;

    // A batch this browser was sending when the page was left: reopen it even if the
    // server finished it, so the files that never reached the server are listed
    const reopenStoredBatch = async (): Promise<boolean> => {
      const stored = userId ? storedBatchToReopen(userId) : null;
      if (!stored) return false;
      let rows = rowsFromStoredBatch(stored, isStillSending(stored));
      try {
        const response = await api.get('/reports/upload-status', { params: { batch: stored.batchId } });
        const snapshot = parseUploadStatus(response.data);
        if (snapshot && (snapshot.batchId === null || snapshot.batchId === stored.batchId)) {
          rows = mergeBatchRows(rows, snapshot.reports);
        }
      } catch (error) {
        console.error('Failed to fetch upload status:', error);
      }
      if (cancelled || interactedRef.current) return true;
      startTracking(stored.batchId, rows);
      return true;
    };

    const resumeLatestBatch = async () => {
      try {
        const response = await api.get('/reports/upload-status', { params: { batch: 'latest' } });
        const snapshot = parseUploadStatus(response.data);
        if (cancelled || interactedRef.current || !snapshot || !isResumableLatestBatch(snapshot)) return;
        startTracking(snapshot.batchId as string, mergeBatchRows([], snapshot.reports));
      } catch (error) {
        console.error('Failed to fetch latest upload status:', error);
      }
    };

    const fetchConfig = async () => {
      try {
        const response = await api.get('/lab-config/upload-config');
        if (cancelled) return;
        if (response.data.success) {
          const config = parseUploadConfig(response.data.data);
          setUploadConfig(config);
          if (await reopenStoredBatch()) return;
          if (config.serverQueue) await resumeLatestBatch();
        }
      } catch (error) {
        console.error('Failed to fetch upload config:', error);
      }
    };
    fetchConfig();

    return () => {
      cancelled = true;
    };
  }, [startTracking, userId]);

  // Step 3 polling: rebuild statuses from the server until every report is final
  useEffect(() => {
    if (!trackedBatchId) return undefined;

    let cancelled = false;
    const trackingStartedMs = Date.now();
    let finished = isPollingDone(batchRowsRef.current, trackingStartedMs);
    // "Processed X/N" only for a batch that finishes while this view is open
    let announced = summariseRows(batchRowsRef.current).allFinal;
    let failures = 0;
    let serverNowMs: number | null = null;
    let inFlight = false;
    let timer: number | undefined;

    const announceFinished = (rows: BatchRow[]) => {
      const summary = summariseRows(rows);
      enqueueSnackbar(`Processed ${summary.readyCount}/${summary.total} reports!`, {
        variant: summary.allReady ? 'success' : 'warning',
      });
    };

    const poll = async (): Promise<void> => {
      if (cancelled || finished || inFlight) return;
      inFlight = true;
      // A /process call that answered after this moment is not reflected in this status
      const requestedAtMs = Date.now();
      try {
        const response = await api.get('/reports/upload-status', { params: { batch: trackedBatchId } });
        if (cancelled) return;
        const snapshot = parseUploadStatus(response.data);
        if (!snapshot || (snapshot.batchId !== null && snapshot.batchId !== trackedBatchId)) {
          // Unusable reply (e.g. an older backend): keep what is shown and try again later
          failures += 1;
        } else {
          failures = 0;
          serverNowMs = snapshot.serverTimeMs;
          const merged = mergeBatchRows(batchRowsRef.current, snapshot.reports);
          const stored = userId ? loadStoredBatch(userId, trackedBatchId) : null;
          const sending = stored ? isStillSending(stored) : isBatchUploading(trackedBatchId);
          const rows = holdRowsBeingStarted(
            settlePendingRows(merged, stored, sending),
            processStartsRef.current,
            requestedAtMs
          );
          forgetAnsweredStarts(processStartsRef.current, requestedAtMs);
          batchRowsRef.current = rows;
          setBatchRows(rows);
          // Finished with nothing missing: the stored record is no longer needed
          if (userId && canClearStoredBatch(rows, stored, trackedBatchId, sending)) {
            clearStoredBatch(userId, trackedBatchId);
          }
          if (!announced && summariseRows(rows).allFinal) {
            announced = true;
            announceFinished(rows);
          }
        }
      } catch {
        // A failed poll is not shown to the nurse; it is simply retried with back-off
        failures += 1;
      } finally {
        inFlight = false;
      }
      finished = isPollingDone(batchRowsRef.current, trackingStartedMs);
      if (!cancelled && !finished) {
        schedule(nextPollDelay(failures, document.hidden, batchRowsRef.current, serverNowMs ?? Date.now()));
        serverNowMs = null;
      }
    };

    const schedule = (delay: number) => {
      window.clearTimeout(timer);
      timer = window.setTimeout(poll, delay);
    };

    // Poll right away when the tab becomes visible again
    const onVisibilityChange = () => {
      if (!document.hidden && !cancelled && !finished) schedule(0);
    };

    document.addEventListener('visibilitychange', onVisibilityChange);
    pollNowRef.current = () => {
      if (!cancelled && !finished) schedule(0);
    };
    poll();

    return () => {
      cancelled = true;
      window.clearTimeout(timer);
      document.removeEventListener('visibilitychange', onVisibilityChange);
      pollNowRef.current = null;
    };
  }, [trackedBatchId, enqueueSnackbar, userId]);

  React.useEffect(() => {
    if (batchStartTime && processing) {
      const interval = setInterval(() => {
        const elapsed = (Date.now() - batchStartTime) / 1000;
        setCurrentElapsedTime(elapsed);
      }, 100);

      return () => clearInterval(interval);
    } else {
      setCurrentElapsedTime(0);
    }
  }, [batchStartTime, processing]);

  const onDrop = useCallback(
    (acceptedFiles: File[], fileRejections: RejectedFile[] = []) => {
      console.log('[UPLOAD] Files selected:', acceptedFiles.length);

      const pdfFiles = acceptedFiles.filter((file) => file.type === 'application/pdf');
      const problems = sortOutSelection(fileRejections, acceptedFiles.length - pdfFiles.length);

      // Every selection warning from this drop goes into one message (notistack shows at most three)
      const warnings: string[] = [];
      if (problems.tooLarge.length > 0) warnings.push(tooLargeMessage(problems.tooLarge));

      if (pdfFiles.length > 0) {
        interactedRef.current = true;
        if (problems.notPdf > 0) warnings.push(NOT_PDF_MESSAGE);

        // If bulk upload is disabled, only take the first file
        // If bulk upload is enabled, limit to maxBulkFiles
        let filesToUse: File[];
        if (bulkUploadDisabled) {
          filesToUse = [pdfFiles[0]];
        } else if (pdfFiles.length > maxBulkFiles) {
          filesToUse = pdfFiles.slice(0, maxBulkFiles);
          warnings.push(`Maximum ${maxBulkFiles} files allowed. Only first ${maxBulkFiles} files will be uploaded.`);
        } else {
          filesToUse = pdfFiles;
        }
        if (warnings.length > 0) {
          enqueueSnackbar(combineSelectionWarnings(warnings), { variant: 'warning' });
        }

        setFiles(filesToUse);
        setUploadItems([]);
        setActiveStep(1);
        enqueueSnackbar(`${filesToUse.length} PDF file(s) selected successfully`, {
          variant: 'success',
        });
      } else {
        // Nothing usable was picked (a drop of only too-large PDFs needs no PDF reminder)
        const notPdf = problems.tooLarge.length === 0 || problems.notPdf > 0 || problems.other > 0;
        if (notPdf) warnings.push(NOT_PDF_MESSAGE);
        enqueueSnackbar(combineSelectionWarnings(warnings), { variant: notPdf ? 'error' : 'warning' });
      }
    },
    [enqueueSnackbar, bulkUploadDisabled, maxBulkFiles]
  );

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'application/pdf': ['.pdf'],
    },
    multiple: !bulkUploadDisabled,
    maxFiles: bulkUploadDisabled ? 1 : maxBulkFiles,
    maxSize: MAX_ACCEPTED_FILE_BYTES,
  });

  const removeFile = (index: number) => {
    const newFiles = files.filter((_, i) => i !== index);
    setFiles(newFiles);
    if (newFiles.length === 0) {
      setActiveStep(0);
    }
  };

  const setUploadItem = (index: number, item: UploadItem) => {
    setUploadItems((previous) => previous.map((current, i) => (i === index ? item : current)));
  };

  // Bulk mode: one request per file, a few at a time; the server queues processing
  const handleBulkUpload = async () => {
    const selectedFiles = files;
    const batchId = generateUploadBatchId();
    const outcomes: BulkUploadOutcome[] = selectedFiles.map(() => ({ ok: false, error: 'Upload failed' }));
    // Set when the server refuses the whole batch: the files not yet sent are not attempted
    let batchRefusal: string | null = null;

    setUploading(true);
    setUploadItems(selectedFiles.map(() => ({ state: 'pending' })));

    // Remember the selection, so a page left mid-upload can show what never reached the server
    if (userId) startStoredBatch(userId, batchId, selectedFiles);
    const uploadSession = beginBatchUpload(batchId, userId);

    try {
      await runWithConcurrency(selectedFiles.length, BULK_UPLOAD_CONCURRENCY, async (index) => {
        const file = selectedFiles[index];

        const fail = (error: string) => {
          outcomes[index] = { ok: false, error };
          setUploadItem(index, { state: 'failed', error });
          // Kept with the file, so a return visit shows Failed with this reason, not Not uploaded
          if (userId) failStoredFile(userId, batchId, index, error);
        };

        if (batchRefusal) {
          fail(batchRefusal);
          return;
        }

        setUploadItem(index, { state: 'uploading', progress: 0 });
        let lastProgress = 0;

        try {
          const formData = new FormData();
          // The batch id goes before the file so the server has it when the file arrives
          formData.append('uploadBatchId', batchId);
          formData.append('pdfs', file);

          const response = await api.post('/reports/upload/multiple', formData, {
            headers: {
              'Content-Type': 'multipart/form-data',
            },
            onUploadProgress: (event) => {
              uploadSession.touch();
              if (!event.total) return;
              const progress = Math.min(100, Math.round((event.loaded / event.total) * 100));
              if (progress !== lastProgress) {
                lastProgress = progress;
                setUploadItem(index, { state: 'uploading', progress });
              }
            },
          });

          const data = response.data?.data;
          const report = data?.uploadedReports?.[0];
          if (!response.data?.success || !report?.reportId) {
            fail(data?.errors?.[0]?.error || response.data?.message || 'Upload failed');
            return;
          }

          outcomes[index] = {
            ok: true,
            reportId: String(report.reportId),
            fileName: report.fileName || file.name,
            fileSize: typeof report.fileSize === 'number' ? report.fileSize : file.size,
            queued: data.queued === true,
          };
          if (userId) acknowledgeStoredFile(userId, batchId, index, String(report.reportId));
          setUploadItem(index, { state: 'uploaded' });
        } catch (error) {
          const { message, stopBatch } = describeUploadError(error, maxBulkFiles);
          if (stopBatch && !batchRefusal) batchRefusal = message;
          fail(message);
        }
      });
    } finally {
      uploadSession.end();
    }

    setUploading(false);

    const uploadedCount = outcomes.filter((outcome) => outcome.ok).length;
    if (uploadedCount === 0) {
      const firstFailure = outcomes.find((outcome) => !outcome.ok);
      enqueueSnackbar(firstFailure && !firstFailure.ok ? firstFailure.error : 'Upload failed', {
        variant: 'error',
      });
      return;
    }

    enqueueSnackbar(`Successfully uploaded ${uploadedCount} report(s)! Processing...`, {
      variant: uploadedCount === selectedFiles.length ? 'success' : 'warning',
    });

    // queued:false means the server is not queueing these reports (switch turned off): keep today's flow and
    // ask it to process them, at most BULK_UPLOAD_CONCURRENCY at a time (each runs inside its request).
    // Until a report's call answers, step 3 does not take the server's Not started as final (holdRowsBeingStarted).
    const toStart: string[] = [];
    outcomes.forEach((outcome) => {
      if (outcome.ok && !outcome.queued) toStart.push(outcome.reportId);
    });
    toStart.forEach((reportId) => processStartsRef.current.set(reportId, null));
    const startQueue = toStart.slice();
    runWithConcurrency(toStart.length, BULK_UPLOAD_CONCURRENCY, async () => {
      const reportId = startQueue.shift() as string;
      try {
        await api.post(`/reports/${reportId}/process`);
      } catch (error) {
        console.error('[UPLOAD] Failed to start processing:', error);
      } finally {
        processStartsRef.current.set(reportId, Date.now());
        pollNowRef.current?.();
      }
    });

    startTracking(
      batchId,
      outcomes.map((outcome, index) =>
        outcome.ok
          ? initialRowFromUpload(outcome)
          : uploadFailureRow(index, selectedFiles[index], outcome.error)
      )
    );
  };

  const handleUpload = async () => {
    if (files.length === 0) {
      enqueueSnackbar('Please select at least one file', { variant: 'error' });
      return;
    }

    interactedRef.current = true;

    if (!bulkUploadDisabled) {
      await handleBulkUpload();
      return;
    }

    setUploading(true);
    const batchId = generateUploadBatchId();
    const formData = new FormData();
    formData.append('uploadBatchId', batchId);

    files.forEach((file) => {
      formData.append('pdfs', file);
    });

    try {
      const response = await api.post('/reports/upload/multiple', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      if (response.data.success) {
        const reports = response.data.data.uploadedReports;

        // The server queued processing: show its statuses instead of driving processing from this tab
        if (response.data.data.queued === true) {
          enqueueSnackbar(`Successfully uploaded ${reports.length} report(s)! Processing...`, {
            variant: 'success',
          });
          startTracking(
            response.data.data.uploadBatchId || batchId,
            reports.map((report: any) => initialRowFromUpload(report))
          );
          return;
        }

        const uploadedReportsList: UploadedReport[] = reports.map((report: any) => ({
          reportId: report.reportId,
          fileName: report.fileName,
          fileSize: report.fileSize,
          status: 'uploaded' as const,
        }));

        setUploadedReports(uploadedReportsList);
        setActiveStep(2);
        enqueueSnackbar(`Successfully uploaded ${reports.length} report(s)! Processing...`, {
          variant: 'success',
        });

        await handleBatchProcessAuto(uploadedReportsList, batchId);
      }
    } catch (error: any) {
      enqueueSnackbar(error.response?.data?.message || 'Upload failed', { variant: 'error' });
    } finally {
      setUploading(false);
    }
  };

  const handleBatchProcessAuto = async (reports: UploadedReport[], batchId: string) => {
    if (reports.length === 0) return;

    setProcessing(true);
    const batchStart = Date.now();
    setBatchStartTime(batchStart);

    // Initialize all reports as 'processing'
    const processingReports = reports.map((r) => ({ ...r, status: 'processing' as const }));
    setUploadedReports(processingReports);

    // Track results as they complete
    const resultsRef: UploadedReport[] = [...processingReports];
    let completedCount = 0;
    // Set if the server answers 202 queued: processing then happens on the server, not in this request
    let queuedByServer = false;

    // Process each report individually for real-time updates
    const processReport = async (report: UploadedReport, index: number) => {
      const startTime = Date.now();
      try {
        const response = await api.post(`/reports/${report.reportId}/process`);
        const processingTime = (Date.now() - startTime) / 1000;

        if (response.data.success && response.data.queued === true) {
          queuedByServer = true;
          resultsRef[index] = { ...report, status: 'uploaded' as const };
        } else if (response.data.success) {
          resultsRef[index] = {
            ...report,
            status: 'completed' as const,
            processingTime,
          };
        } else {
          resultsRef[index] = {
            ...report,
            status: 'error' as const,
            error: response.data.message || 'Processing failed',
          };
        }
      } catch (error: any) {
        resultsRef[index] = {
          ...report,
          status: 'error' as const,
          error: error.response?.data?.message || 'Processing failed',
        };
      }

      completedCount++;
      setCurrentProcessingIndex(completedCount);

      // Update state immediately so UI reflects completed reports
      setUploadedReports([...resultsRef]);

      // Show notification for first completed report
      if (completedCount === 1 && resultsRef[index].status === 'completed') {
        enqueueSnackbar(`${report.fileName} ready for review!`, { variant: 'success' });
      }
    };

    // Start all processing in parallel. The backend caps page conversions and Gemini calls, not reports in flight
    await Promise.all(reports.map((report, index) => processReport(report, index)));

    if (queuedByServer) {
      // Show the server's statuses for this upload instead of a local result
      setProcessing(false);
      setBatchStartTime(null);
      startTracking(batchId, reports.map((report) => initialRowFromUpload(report)));
      return;
    }

    // All done
    setProcessing(false);
    setBatchStartTime(null);
    setShowSuccessDialog(true);

    const successCount = resultsRef.filter((r) => r.status === 'completed').length;
    enqueueSnackbar(
      `Processed ${successCount}/${reports.length} reports!`,
      { variant: successCount === reports.length ? 'success' : 'warning' }
    );
  };

  const handleReset = () => {
    // "Upload New Reports": forget the batch on screen (only that one), unless a tab is still sending it
    // (that run clears its own record when it ends with nothing missing)
    if (userId && trackedBatchId) {
      const stored = loadStoredBatch(userId, trackedBatchId);
      if (stored && !isStillSending(stored)) clearStoredBatch(userId, trackedBatchId);
    }
    setFiles([]);
    setUploadedReports([]);
    setUploadItems([]);
    stopTracking();
    setActiveStep(0);
    setUploading(false);
    setProcessing(false);
    setShowSuccessDialog(false);
    setBatchStartTime(null);
    setCurrentProcessingIndex(-1);
  };

  const handleReviewReports = () => {
    navigate('/nurse/reports');
  };

  const batchSummary = summariseRows(batchRows);
  const notUploadedCount = countNotUploaded(batchRows);

  const steps = [
    {
      label: 'Select PDF Files',
      description: 'Choose one or more PDF files to upload',
    },
    {
      label: 'Review & Upload',
      description: 'Review settings and upload your reports',
    },
    {
      label: 'Processing',
      description: 'Reports are being processed automatically',
    },
  ];

  return (
    <div
      style={{
        fontFamily: theme.typography.fontFamily,
      }}
    >
      {/* Header */}
      <div style={{ marginBottom: theme.spacing.xl }}>
        <h1
          style={{
            fontSize: theme.typography.sizes.heading,
            fontWeight: theme.typography.weights.bold,
            color: theme.colors.textPrimary,
            margin: 0,
            marginBottom: theme.spacing.xs,
            lineHeight: theme.typography.lineHeights.tight,
          }}
        >
          Upload Lab Reports
        </h1>
        <p
          style={{
            fontSize: theme.typography.sizes.body,
            fontWeight: theme.typography.weights.normal,
            color: theme.colors.textSecondary,
            margin: 0,
            lineHeight: theme.typography.lineHeights.normal,
          }}
        >
          Upload {bulkUploadDisabled ? 'a' : `up to ${maxBulkFiles}`} lab report PDF{bulkUploadDisabled ? '' : 's'} for AI-powered data extraction
        </p>
      </div>

      {/* Main Card */}
      <div
        style={{
          backgroundColor: theme.colors.surface,
          borderRadius: theme.radius.lg,
          padding: theme.spacing.xl,
          boxShadow: theme.shadows.md,
          maxWidth: '900px',
        }}
      >
        {/* Step Indicators */}
        <div style={{ marginBottom: theme.spacing.xl }}>
          <div style={{ display: 'flex', gap: theme.spacing.md }}>
            {steps.map((step, index) => {
              const isActive = index === activeStep;
              const isCompleted = index < activeStep;

              return (
                <div key={index} style={{ flex: 1 }}>
                  <div
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      gap: theme.spacing.xs,
                    }}
                  >
                    <div
                      style={{
                        width: '40px',
                        height: '40px',
                        borderRadius: '50%',
                        backgroundColor: isActive
                          ? theme.colors.accent
                          : isCompleted
                          ? theme.colors.success
                          : theme.colors.background,
                        border: `2px solid ${
                          isActive || isCompleted ? 'transparent' : theme.colors.border
                        }`,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: theme.typography.sizes.body,
                        fontWeight: theme.typography.weights.semibold,
                        color:
                          isActive || isCompleted
                            ? theme.colors.surface
                            : theme.colors.textSecondary,
                      }}
                    >
                      {isCompleted ? '✓' : index + 1}
                    </div>
                    <div
                      style={{
                        fontSize: theme.typography.sizes.small,
                        fontWeight: isActive
                          ? theme.typography.weights.semibold
                          : theme.typography.weights.medium,
                        color: isActive ? theme.colors.textPrimary : theme.colors.textSecondary,
                        textAlign: 'center',
                      }}
                    >
                      {step.label}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Step Content */}
        <div>
          {/* STEP 1: File Selection */}
          {activeStep === 0 && (
            <div>
              <DropZone
                onDrop={onDrop}
                multiple={!bulkUploadDisabled}
                maxSize={MAX_ACCEPTED_FILE_BYTES}
                maxFiles={bulkUploadDisabled ? 1 : maxBulkFiles}
              />
            </div>
          )}

          {/* STEP 1.5: Files Selected */}
          {activeStep === 1 && (
            <div>
              <div
                style={{
                  marginBottom: theme.spacing.md,
                  padding: theme.spacing.md,
                  backgroundColor: theme.colors.background,
                  borderRadius: theme.radius.md,
                }}
              >
                <div
                  style={{
                    fontSize: theme.typography.sizes.body,
                    fontWeight: theme.typography.weights.semibold,
                    color: theme.colors.textPrimary,
                    marginBottom: theme.spacing.xs,
                  }}
                >
                  Selected Files ({files.length})
                </div>
                <div
                  style={{
                    fontSize: theme.typography.sizes.small,
                    color: theme.colors.textSecondary,
                  }}
                >
                  Total size:{' '}
                  {formatFileSize(files.reduce((acc, file) => acc + file.size, 0))}
                </div>
              </div>

              <FileList
                files={files.map((file, index) => ({
                  name: file.name,
                  size: file.size,
                  ...(index < uploadItems.length ? toUploadFileStatus(uploadItems[index]) : {}),
                }))}
                onRemove={removeFile}
                showStatus={uploadItems.length > 0}
              />

              <AlertBox variant="info" action={undefined}>
                <div>
                  <strong>Processing Configuration:</strong>
                  <br />
                  Reports will be processed as per the configuration set by your admin.
                </div>
              </AlertBox>

              <div
                style={{
                  display: 'flex',
                  gap: theme.spacing.sm,
                  marginTop: theme.spacing.lg,
                }}
              >
                <CustomButton
                  variant="secondary"
                  onClick={() => setActiveStep(0)}
                  disabled={!bulkUploadDisabled && uploading}
                >
                  Back
                </CustomButton>
                <CustomButton
                  variant="primary"
                  onClick={handleUpload}
                  disabled={uploading || processing}
                  loading={uploading}
                >
                  {uploading
                    ? `Uploading ${files.length} File${files.length !== 1 ? 's' : ''}...`
                    : processing
                    ? 'Processing...'
                    : `Upload & Process ${files.length} Report${files.length !== 1 ? 's' : ''}`}
                </CustomButton>
              </div>
            </div>
          )}

          {/* STEP 2: Processing queued on the server; statuses come from the server */}
          {activeStep === 2 && trackedBatchId && (
            <div>
              {!batchSummary.allFinal && (
                <div style={{ marginBottom: theme.spacing.lg }}>
                  <ProgressBar
                    value={batchSummary.finalCount}
                    max={batchSummary.total}
                    label={`${batchSummary.finalCount} of ${batchSummary.total} completed`}
                  />
                  <div
                    style={{
                      fontSize: theme.typography.sizes.small,
                      marginTop: theme.spacing.xs,
                      color: theme.colors.success,
                    }}
                  >
                    {batchSummary.readyCount > 0 && `${batchSummary.readyCount} ready for review`}
                  </div>
                  <div
                    style={{
                      fontSize: theme.typography.sizes.small,
                      marginTop: theme.spacing.xs,
                      color: theme.colors.textSecondary,
                    }}
                  >
                    You can leave this page. Processing continues on the server.
                  </div>
                </div>
              )}

              {notUploadedCount > 0 && (
                <div style={{ marginBottom: theme.spacing.md }}>
                  <AlertBox variant="warning">{notUploadedMessage(notUploadedCount)}</AlertBox>
                </div>
              )}

              <FileList files={batchRows.map(toBatchFileItem)} showStatus />

              {/* Always mounted so screen readers hear the summary when it changes (not on unchanged polls) */}
              <div role="status" aria-live="polite" aria-atomic="true">
                {batchSummary.finalCount > 0 && (
                  <AlertBox
                    variant={
                      batchSummary.allReady
                        ? 'success'
                        : batchSummary.readyCount > 0
                        ? 'info'
                        : 'warning'
                    }
                  >
                    {!batchSummary.allFinal ? (
                      <>
                        <strong>{batchSummary.readyCount}</strong> of {batchSummary.total} reports ready for
                        review.{' '}
                        <span style={{ color: theme.colors.textSecondary }}>
                          ({batchSummary.total - batchSummary.finalCount} still processing...)
                        </span>
                      </>
                    ) : (
                      <>
                        {batchSummary.readyCount} of {batchSummary.total} reports processed successfully
                      </>
                    )}
                  </AlertBox>
                )}
              </div>

              <div
                style={{
                  display: 'flex',
                  gap: theme.spacing.sm,
                  marginTop: theme.spacing.lg,
                }}
              >
                <CustomButton variant="secondary" onClick={handleReset}>
                  Upload New Reports
                </CustomButton>
                <CustomButton variant="primary" onClick={handleReviewReports}>
                  {!batchSummary.allFinal && batchSummary.readyCount > 0
                    ? `Review ${batchSummary.readyCount} Ready Report${batchSummary.readyCount !== 1 ? 's' : ''}`
                    : 'Review Reports'}
                </CustomButton>
              </div>
            </div>
          )}

          {/* STEP 2: Processing */}
          {activeStep === 2 && !trackedBatchId && (
            <div>
              {processing && (
                <div style={{ marginBottom: theme.spacing.lg }}>
                  <ProgressBar
                    value={currentProcessingIndex}
                    max={uploadedReports.length}
                    label={`${currentProcessingIndex} of ${uploadedReports.length} completed`}
                  />
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      fontSize: theme.typography.sizes.small,
                      marginTop: theme.spacing.xs,
                    }}
                  >
                    <span style={{ color: theme.colors.success }}>
                      {currentProcessingIndex > 0 && `${currentProcessingIndex} ready for review`}
                    </span>
                    <span style={{ color: theme.colors.accent }}>
                      {currentElapsedTime.toFixed(1)}s elapsed
                    </span>
                  </div>
                </div>
              )}

              <FileList
                files={uploadedReports.map(r => ({
                  name: r.fileName,
                  size: r.fileSize,
                  status: r.status,
                  error: r.error,
                  processingTime: r.processingTime
                }))}
                showStatus
              />

              {/* Show status alert - updates in real-time */}
              {uploadedReports.some((r) => r.status === 'completed' || r.status === 'error') && (
                <AlertBox
                  variant={
                    !processing && uploadedReports.every((r) => r.status === 'completed')
                      ? 'success'
                      : uploadedReports.some((r) => r.status === 'completed')
                      ? 'info'
                      : 'warning'
                  }
                >
                  {processing ? (
                    <>
                      <strong>{uploadedReports.filter((r) => r.status === 'completed').length}</strong> of{' '}
                      {uploadedReports.length} reports ready for review.{' '}
                      <span style={{ color: theme.colors.textSecondary }}>
                        ({uploadedReports.filter((r) => r.status === 'processing').length} still processing...)
                      </span>
                    </>
                  ) : (
                    <>
                      {uploadedReports.filter((r) => r.status === 'completed').length} of{' '}
                      {uploadedReports.length} reports processed successfully
                    </>
                  )}
                </AlertBox>
              )}

              <div
                style={{
                  display: 'flex',
                  gap: theme.spacing.sm,
                  marginTop: theme.spacing.lg,
                }}
              >
                <CustomButton variant="secondary" onClick={handleReset} disabled={processing}>
                  Upload New Reports
                </CustomButton>
                <CustomButton
                  variant="primary"
                  onClick={handleReviewReports}
                  disabled={!uploadedReports.some((r) => r.status === 'completed')}
                >
                  {processing && uploadedReports.some((r) => r.status === 'completed')
                    ? `Review ${uploadedReports.filter((r) => r.status === 'completed').length} Ready Report${uploadedReports.filter((r) => r.status === 'completed').length !== 1 ? 's' : ''}`
                    : 'Review Reports'}
                </CustomButton>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Success Modal */}
      <Modal
        open={showSuccessDialog}
        onClose={() => setShowSuccessDialog(false)}
        title="Batch Processing Complete"
        maxWidth="sm"
        actions={
          <>
            <CustomButton variant="secondary" onClick={handleReset}>
              Upload More Reports
            </CustomButton>
            <CustomButton variant="primary" onClick={handleReviewReports}>
              Review Reports
            </CustomButton>
          </>
        }
      >
        <div>
          <p
            style={{
              fontSize: theme.typography.sizes.body,
              color: theme.colors.textPrimary,
              margin: 0,
              marginBottom: theme.spacing.md,
            }}
          >
            {uploadedReports.filter((r) => r.status === 'completed').length} of{' '}
            {uploadedReports.length} reports processed successfully!
          </p>

          <div
            style={{
              fontSize: theme.typography.sizes.small,
              fontWeight: theme.typography.weights.semibold,
              color: theme.colors.textSecondary,
              marginBottom: theme.spacing.sm,
            }}
          >
            Processing Summary:
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: theme.spacing.xs }}>
            {uploadedReports.map((report, index) => (
              <div
                key={index}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: theme.spacing.sm,
                  padding: theme.spacing.xs,
                  backgroundColor: theme.colors.background,
                  borderRadius: theme.radius.sm,
                }}
              >
                <span style={{ fontSize: '16px' }}>
                  {report.status === 'completed' ? '✓' : '✕'}
                </span>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div
                    style={{
                      fontSize: theme.typography.sizes.small,
                      color: theme.colors.textPrimary,
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                    }}
                  >
                    {report.fileName}
                  </div>
                  <div
                    style={{
                      fontSize: theme.typography.sizes.tiny,
                      color: theme.colors.textSecondary,
                    }}
                  >
                    {report.status === 'completed'
                      ? `${report.processingTime?.toFixed(2)}s`
                      : report.error}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default UploadReport;
